import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SectionCard } from "../components/SectionCard";
import { ToggleRow } from "../components/ToggleRow";
import { useAuth } from "../context/AuthContext";
import { useSetTrail } from "../context/BreadcrumbContext";
import { api } from "../lib/api";
import type { TournamentSummary } from "../types/domain";

type GameMode = "quick" | "tournament";

interface PlayerOption {
  id: string;
  pseudo: string;
}

interface PlayerDropdownProps {
  value: string;
  placeholder: string;
  players: PlayerOption[];
  excluded: string[];
  onChange: (pseudo: string) => void;
}

function PlayerDropdown({
  value,
  placeholder,
  players,
  excluded,
  onChange,
}: PlayerDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const available = players.filter(
    (p) =>
      !excluded.includes(p.pseudo) &&
      p.pseudo.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="text-field w-full text-left"
        onClick={() => {
          setOpen(!open);
          setSearch("");
        }}
      >
        {value || <span className="text-gray-400">{placeholder}</span>}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="p-2">
            <input
              type="text"
              className="text-field w-full text-sm"
              placeholder="Rechercher..."
              value={search}
              autoFocus
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className="max-h-48 overflow-y-auto">
            {available.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className="w-full px-4 py-2 text-left text-sm text-ink hover:bg-gray-50"
                  onClick={() => {
                    onChange(p.pseudo);
                    setOpen(false);
                  }}
                >
                  {p.pseudo}
                </button>
              </li>
            ))}
            {available.length === 0 && (
              <li className="px-4 py-2 text-sm text-smoke">Aucun joueur</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

interface TeamOption {
  teamId: string;
  teamName: string;
}

interface TeamDropdownProps {
  value: string;
  teams: TeamOption[];
  onChange: (teamId: string) => void;
}

export function TeamDropdown({ value, teams, onChange }: TeamDropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = teams.filter((t) =>
    t.teamName.toLowerCase().includes(search.toLowerCase()),
  );
  const selectedTeam = teams.find((t) => t.teamId === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={`text-field w-full text-left ${value ? "border-ember" : ""}`}
        onClick={() => {
          setOpen(!open);
          setSearch("");
        }}
      >
        {selectedTeam ? (
          selectedTeam.teamName
        ) : (
          <span className="text-gray-400">Équipe adverse</span>
        )}
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg">
          <div className="p-2">
            <input
              type="text"
              className="text-field w-full text-sm"
              placeholder="Rechercher..."
              value={search}
              autoFocus
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className="max-h-48 overflow-y-auto">
            {filtered.map((t) => (
              <li key={t.teamId}>
                <button
                  type="button"
                  className="w-full px-4 py-2 text-left text-sm text-ink hover:bg-gray-50"
                  onClick={() => {
                    onChange(t.teamId);
                    setOpen(false);
                  }}
                >
                  {t.teamName}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-4 py-2 text-sm text-smoke">Aucune équipe</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

const TARGET_SCORES = [500, 1000, 1500, 2000, 2500, 3000];

export function NewGamePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mode, setMode] = useState<GameMode>("quick");

  useSetTrail([
    { label: "Parties", to: "/games" },
    { label: "Nouvelle partie" },
  ]);

  // Quick game state
  const [players, setPlayers] = useState<PlayerOption[]>([]);
  const [teamLeft, setTeamLeft] = useState([user?.pseudo ?? "", ""]);
  const [teamRight, setTeamRight] = useState(["", ""]);
  const [targetScore, setTargetScore] = useState(2000);
  const [sansAtoutEnabled, setSansAtoutEnabled] = useState(true);
  const [toutAtoutEnabled, setToutAtoutEnabled] = useState(true);
  const [surcoincheEnabled, setSurcoincheEnabled] = useState(true);

  // Tournament game state
  const [tournaments, setTournaments] = useState<TournamentSummary[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState("");
  const [opponentTeamId, setOpponentTeamId] = useState("");
  const [blank, setBlank] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void api.listPlayers().then(setPlayers);
    void api.listTournaments().then((all) => {
      const eligible = all.filter(
        (t) => t.status === "in_progress" && t.myTeamId,
      );
      setTournaments(eligible);
      if (eligible.length === 1) {
        setSelectedTournamentId(eligible[0].id);
      }
    });
  }, []);

  const selectedTournament = tournaments.find(
    (t) => t.id === selectedTournamentId,
  );
  const myTeamId = selectedTournament?.myTeamId ?? null;

  // We need standings for team selection. TournamentSummary doesn't have them,
  // but TournamentDetail does. Fetch detail when tournament is selected.
  const [tournamentStandings, setTournamentStandings] = useState<
    Array<{ teamId: string; teamName: string }>
  >([]);

  useEffect(() => {
    if (!selectedTournamentId) {
      setTournamentStandings([]);
      return;
    }
    void api.getTournament(selectedTournamentId).then((detail) => {
      setTournamentStandings(
        detail.standingRows.map((r) => ({
          teamId: r.teamId,
          teamName: r.teamName,
        })),
      );
    });
  }, [selectedTournamentId]);

  const opponentOptions = tournamentStandings.filter(
    (t) => t.teamId !== myTeamId,
  );
  const myTeamName = tournamentStandings.find(
    (t) => t.teamId === myTeamId,
  )?.teamName;

  const selectedPseudos = [...teamLeft, ...teamRight].filter(Boolean);
  const allPlayersSelected =
    teamLeft.filter(Boolean).length === 2 &&
    teamRight.filter(Boolean).length === 2;

  function handleQuickSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    void api
      .createQuickGame({
        teamLeftPseudos: teamLeft.filter(Boolean),
        teamRightPseudos: teamRight.filter(Boolean),
        targetScore,
        ruleset: {
          beloteEnabled: true,
          sansAtoutEnabled,
          toutAtoutEnabled,
          surcoincheEnabled,
          foulEnabled: true,
        },
      })
      .then((game) => navigate(`/games/${game.id}`))
      .catch(() => {})
      .finally(() => setSubmitting(false));
  }

  function handleTournamentSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!selectedTournamentId || !opponentTeamId) return;
    setSubmitting(true);
    void api
      .createTournamentGame({
        tournamentId: selectedTournamentId,
        opponentTeamId,
        blank,
      })
      .then((game) => navigate(`/games/${game.id}`))
      .catch(() => {})
      .finally(() => setSubmitting(false));
  }

  return (
    <div className="sm:max-w-lg">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-ink">Nouvelle partie</h1>
      </div>

      {/* Mode selector */}
      <div className="mb-5 grid grid-cols-2 gap-3">
        <button
          type="button"
          className={`card text-left transition ${
            mode === "quick"
              ? "border-ember ring-1 ring-ember"
              : "hover:border-ember/40"
          }`}
          onClick={() => setMode("quick")}
        >
          <p className="font-semibold text-ink">Partie rapide</p>
          <p className="mt-1 text-xs text-smoke">4 joueurs, hors tournoi</p>
        </button>
        <button
          type="button"
          disabled={tournaments.length === 0}
          className={`card text-left transition ${
            mode === "tournament"
              ? "border-ember ring-1 ring-ember"
              : tournaments.length === 0
                ? "opacity-40 cursor-not-allowed"
                : "hover:border-ember/40"
          }`}
          onClick={() => {
            if (tournaments.length > 0) setMode("tournament");
          }}
        >
          <p className="font-semibold text-ink">Partie de tournoi</p>
          <p className="mt-1 text-xs text-smoke">
            {tournaments.length === 0
              ? "Aucun tournoi actif"
              : `${tournaments.length} tournoi(s) actif(s)`}
          </p>
        </button>
      </div>

      {/* Quick game form */}
      {mode === "quick" && (
        <form className="space-y-5" onSubmit={handleQuickSubmit}>
          <SectionCard title="Joueurs">
            <div className="space-y-3">
              <div className="rounded-xl bg-gray-50 p-4 space-y-2">
                <p className="mb-1 text-sm font-semibold text-ink">Nous</p>
                <div className="text-field w-full text-left opacity-60">
                  {user?.pseudo}
                </div>
                <PlayerDropdown
                  value={teamLeft[1]}
                  placeholder="Joueur 2"
                  players={players}
                  excluded={selectedPseudos.filter((p) => p !== teamLeft[1])}
                  onChange={(pseudo) => setTeamLeft([teamLeft[0], pseudo])}
                />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-gray-200" />
                <span className="text-sm font-bold text-ink">🔥 vs 🔥</span>
                <div className="h-px flex-1 bg-gray-200" />
              </div>
              <div className="rounded-xl bg-gray-50 p-4 space-y-2">
                <p className="mb-1 text-sm font-semibold text-ink">Eux</p>
                {teamRight.map((player, i) => (
                  <PlayerDropdown
                    key={`right-${i}`}
                    value={player}
                    placeholder={`Joueur ${i + 1}`}
                    players={players}
                    excluded={selectedPseudos.filter((p) => p !== player)}
                    onChange={(pseudo) => {
                      const next = [...teamRight];
                      next[i] = pseudo;
                      setTeamRight(next);
                    }}
                  />
                ))}
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Score cible">
            <div className="space-y-3">
              <div className="grid grid-cols-6 gap-1.5">
                {TARGET_SCORES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`rounded-full border py-2 text-sm font-medium transition ${
                      targetScore === s
                        ? "border-ember bg-ember text-white"
                        : "border-gray-200 bg-white text-ink hover:border-ember/40"
                    }`}
                    onClick={() => setTargetScore(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Règles">
            <div className="space-y-2">
              <ToggleRow
                id="sans-atout"
                label="Sans atout"
                checked={sansAtoutEnabled}
                onChange={setSansAtoutEnabled}
              />
              <ToggleRow
                id="tout-atout"
                label="Tout atout"
                checked={toutAtoutEnabled}
                onChange={setToutAtoutEnabled}
              />
              <ToggleRow
                id="surcoinche"
                label="Surcoinche"
                checked={surcoincheEnabled}
                onChange={setSurcoincheEnabled}
              />
            </div>
          </SectionCard>

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={submitting || !allPlayersSelected}
          >
            {submitting ? "Création en cours..." : "Créer la partie"}
          </button>
        </form>
      )}

      {/* Tournament game form */}
      {mode === "tournament" && (
        <form className="space-y-5" onSubmit={handleTournamentSubmit}>
          {/* Tournament selector - only shown when >1 active tournament */}
          {tournaments.length > 1 && (
            <div className="grid grid-cols-2 gap-3">
              {tournaments.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`card text-left transition ${
                    selectedTournamentId === t.id
                      ? "border-ember ring-1 ring-ember"
                      : "hover:border-ember/40"
                  }`}
                  onClick={() => {
                    setSelectedTournamentId(t.id);
                    setOpponentTeamId("");
                  }}
                >
                  <p className="font-semibold text-ink">{t.name}</p>
                  <p className="mt-0.5 text-xs text-smoke">
                    {t.teamCount} équipes
                  </p>
                </button>
              ))}
            </div>
          )}

          {/* Tournament name */}
          {selectedTournament && (
            <div className="card">
              <p className="text-sm font-semibold text-ink">
                {selectedTournament.name}
              </p>
            </div>
          )}

          {/* Opposition card */}
          {selectedTournament && myTeamName && (
            <SectionCard title="Équipes">
              <div className="space-y-3">
                <div className="rounded-xl bg-gray-50 p-4 space-y-2">
                  <p className="mb-1 text-sm font-semibold text-ink">Nous</p>
                  <div
                    className="text-field w-full bg-gray-50 opacity-70"
                    aria-label="Votre équipe"
                  >
                    {myTeamName}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-gray-200" />
                  <span className="text-sm font-bold text-ink">🔥 vs 🔥</span>
                  <div className="h-px flex-1 bg-gray-200" />
                </div>
                <div className="rounded-xl bg-gray-50 p-4 space-y-2">
                  <p className="mb-1 text-sm font-semibold text-ink">Eux</p>
                  <TeamDropdown
                    value={opponentTeamId}
                    teams={opponentOptions}
                    onChange={setOpponentTeamId}
                  />
                </div>
              </div>
            </SectionCard>
          )}

          {/* Blank game toggle */}
          {selectedTournament && (
            <ToggleRow
              id="blank-game"
              label="Partie à blanc"
              checked={blank}
              onChange={setBlank}
            />
          )}
          {blank && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              L'équipe adverse (support) ne marquera aucun point. Ses levées
              seront comptées comme levées à blanc support.
            </div>
          )}

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={submitting || !selectedTournamentId || !opponentTeamId}
          >
            {submitting
              ? "Création en cours..."
              : blank
                ? "Créer la partie à blanc"
                : "Créer la partie"}
          </button>
        </form>
      )}
    </div>
  );
}
